/**
 * Collection Quick View — Schnellansicht overlay for product cards.
 * Prefetches section HTML for buttons already in the document, then for
 * cards added by infinite scroll or product recommendations.
 */
(function () {
  if (window.collectionQuickViewReady) return;
  window.collectionQuickViewReady = true;

  const SECTION_ID = 'collection-quick-view';
  const SECTION_MARKER = 'id="shopify-section-collection-quick-view"';
  const EXCLUDED_HANDLES = ['hensslers-geschenkgutscheine'];
  const productHtmlByHandle = new Map();
  const productHtmlPending = new Map();

  const rootUrl = () => window.Shopify?.routes?.root || '/';

  function ensureOverlay() {
    let overlay = document.querySelector('.collection-quick-view__overlay');
    if (overlay) return overlay;
    overlay = document.createElement('div');
    overlay.className = 'collection-quick-view__overlay';
    overlay.innerHTML =
      '<div class="collection-quick-view__panel" role="dialog" aria-modal="true" aria-label="Schnellansicht"></div>';
    document.body.appendChild(overlay);
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) setOpen(false);
    });
    return overlay;
  }

  function getPanel() {
    return ensureOverlay().querySelector('.collection-quick-view__panel');
  }

  function setOpen(open) {
    ensureOverlay().classList.toggle('is-open', open);
    document.documentElement.classList.toggle('collection-quick-view--open', open);
  }

  function reinitReviews() {
    if (typeof window.initReviewsIoRatings === 'function') {
      window.initReviewsIoRatings();
      return;
    }
    if (typeof ratingSnippet === 'function') {
      ratingSnippet('ruk_rating_snippet', {
        store: 'henssler.shop',
        mode: 'default',
        color: '#0E1311',
        linebreak: false,
        text: '',
        singularText: '',
        lang: 'de',
        usePolaris: true,
        showEmptyStars: false,
      });
    }
  }

  function bindPanelListeners(handle) {
    const panel = getPanel();
    panel.querySelectorAll('.collection-quick-view__close').forEach((el) => {
      el.addEventListener('click', () => setOpen(false));
    });

    const form = panel.querySelector(`#product-form-${SECTION_ID}`);
    if (form) {
      form.addEventListener('submit', () => {
        setOpen(false);
        document.querySelector('.header__icons .header__icon[href*="/cart"]')?.click();
      });
    }

    const preorderLabel = panel.querySelector(
      '.collection-quick-view-product-quantity-container--preorder .add-to-cart-button .product-form__buttons button > span'
    );
    if (preorderLabel) preorderLabel.textContent = 'Jetzt vorbestellen';

    const mediaModal = panel.querySelector('.collection-quick-view-product-media-modal');
    if (mediaModal && !mediaModal.classList.contains('collection-quick-view-product-media-modal--moved')) {
      mediaModal.classList.add('collection-quick-view-product-media-modal--moved');
      document.body.appendChild(mediaModal);
    }

    panel.querySelectorAll('a.collection-quick-view-product-fake-variant').forEach((link) => {
      link.addEventListener('click', (event) => {
        const href = link.getAttribute('href') || '';
        const match = href.match(/\/products\/([^/?#]+)/);
        if (!match) return;
        event.preventDefault();
        openQuickView(decodeURIComponent(match[1]));
      });
    });

    reinitReviews();
  }

  function populate(handle) {
    const html = productHtmlByHandle.get(handle);
    const panel = getPanel();
    if (!html || !panel) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const shopifySection = doc.querySelector('.shopify-section') || doc.body;
    shopifySection.querySelectorAll('link[rel="stylesheet"], script').forEach((el) => el.remove());
    panel.innerHTML = `
      <button type="button" class="collection-quick-view__close" aria-label="Schließen"></button>
      ${shopifySection.innerHTML}
      <a href="${rootUrl()}products/${handle}" class="collection-quick-view__skip">Zum Artikel</a>
    `;
    bindPanelListeners(handle);
  }

  function fetchProductHtml(handle) {
    if (!handle || EXCLUDED_HANDLES.includes(handle) || productHtmlByHandle.has(handle)) {
      return Promise.resolve();
    }

    const pending = productHtmlPending.get(handle);
    if (pending) return pending;

    const request = fetch(`${rootUrl()}products/${handle}?section_id=${SECTION_ID}`)
      .then((res) => {
        if (!res.ok) throw new Error('quick view request failed');
        return res.text();
      })
      .then((html) => {
        if (!html.includes(SECTION_MARKER)) throw new Error('quick view section missing');
        productHtmlByHandle.set(handle, html);
      })
      .catch(() => {})
      .finally(() => {
        productHtmlPending.delete(handle);
      });

    productHtmlPending.set(handle, request);
    return request;
  }

  function openQuickView(handle) {
    if (!handle || EXCLUDED_HANDLES.includes(handle)) return;
    ensureOverlay();
    if (productHtmlByHandle.has(handle)) {
      populate(handle);
      setOpen(true);
      return;
    }
    fetchProductHtml(handle).then(() => {
      if (!productHtmlByHandle.has(handle)) return;
      populate(handle);
      setOpen(true);
    });
  }

  function handleTriggerClick(event) {
    const trigger = event.target.closest('[data-collection-quick-view]');
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    openQuickView(trigger.getAttribute('data-product-handle'));
  }

  function handlesIn(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return [];
    return [
      ...new Set(
        [...root.querySelectorAll('[data-collection-quick-view][data-product-handle]')]
          .map((el) => el.getAttribute('data-product-handle'))
          .filter(Boolean)
      ),
    ];
  }

  function handleLateCards(event) {
    prefetchHandles(handlesIn(event.target));
  }

  function prefetchHandles(handles) {
    const batchSize = 5;
    let chain = Promise.resolve();
    for (let i = 0; i < handles.length; i += batchSize) {
      const batch = handles.slice(i, i + batchSize);
      chain = chain.then(() => Promise.all(batch.map((handle) => fetchProductHtml(handle))));
    }
    return chain;
  }

  function init() {
    ensureOverlay();
    document.addEventListener('click', handleTriggerClick);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') setOpen(false);
    });

    prefetchHandles(handlesIn(document));
    document.addEventListener('infinite-scroll:products-loaded', handleLateCards);
    document.addEventListener('recommendations:loaded', handleLateCards);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
